"use client"

import { useState, useEffect, useMemo, useCallback } from "react"

export interface SavedCardDisplay {
	id: string
	lastFour: string | null
	brand: string | null // "visa", "mastercard", "amex", "discover"
	expMonth: string
	expYear: string
	cardholderName: string | null
	isPrimary: boolean
}

interface UseSavedCardsReturn {
	savedCards: SavedCardDisplay[]
	isLoading: boolean
	error: string | null
	selectedCardId: string | null
	setSelectedCardId: (id: string | null) => void
	isUsingSavedCard: boolean
	selectedCard: SavedCardDisplay | null
	refetch: () => void
}

export function useSavedCards(userId?: string | null): UseSavedCardsReturn {
	const [savedCards, setSavedCards] = useState<SavedCardDisplay[]>([])
	// Start loading=true when userId exists to prevent flash of card form before saved cards load
	const [isLoading, setIsLoading] = useState(!!userId)
	const [error, setError] = useState<string | null>(null)
	const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

	const fetchCards = useCallback(async () => {
		if (!userId) return
		setIsLoading(true)
		setError(null)
		// Fail-open grace timer (mirrors EvervaultCardForm's onReady FIX-1): if the
		// saved-cards endpoint hangs, abort after 6s so isLoading flips false and the
		// card form reveals its manual-entry fieldset instead of blocking forever.
		const controller = new AbortController()
		const timeout = setTimeout(() => controller.abort(), 6000)
		try {
			const res = await fetch("/api/evervault/saved-cards", { signal: controller.signal })
			if (!res.ok) throw new Error("Failed to fetch saved cards")
			const data = await res.json()
			const cards: SavedCardDisplay[] = data.cards || []
			setSavedCards(cards)
			// Auto-select primary card (first in list, which is primary)
			if (cards.length > 0) {
				setSelectedCardId(cards[0].id)
			}
		} catch (err: any) {
			console.error("[useSavedCards] Fetch error:", err)
			// Timeout/abort is a fail-open, not a user-facing error — leave error null
			// so the form falls back to manual entry silently.
			setError(err?.name === "AbortError" ? null : err.message || "Failed to load saved cards")
			setSavedCards([])
		} finally {
			clearTimeout(timeout)
			setIsLoading(false)
		}
	}, [userId])

	useEffect(() => {
		fetchCards()
	}, [fetchCards])

	const isUsingSavedCard = selectedCardId !== null

	const selectedCard = useMemo(() => savedCards.find((c) => c.id === selectedCardId) ?? null, [savedCards, selectedCardId])

	return {
		savedCards,
		isLoading,
		error,
		selectedCardId,
		setSelectedCardId,
		isUsingSavedCard,
		selectedCard,
		refetch: fetchCards,
	}
}
